import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type Contact,
  ContactService,
  type CreateAssetContactDto,
  type CreateContactDto,
} from '../services/ContactService';

// Query keys
const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (filters: string) => [...contactKeys.lists(), { filters }] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: number) => [...contactKeys.details(), id] as const,
  assetContacts: (assetId: string) =>
    [...contactKeys.all, 'asset', assetId] as const,
};

// Hooks
export const useContacts = () => {
  return useQuery({
    queryKey: contactKeys.lists(),
    queryFn: () => ContactService.getContacts(),
  });
};

export const useContact = (id: number) => {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => ContactService.getContact(id),
    enabled: !!id,
  });
};

export const useAssetContacts = (assetId: string) => {
  return useQuery({
    queryKey: contactKeys.assetContacts(assetId),
    queryFn: () => ContactService.getAssetContacts(assetId),
    enabled: !!assetId,
  });
};

export const useCreateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContactDto) => ContactService.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact created successfully');
    },
    onError: (error) => {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
    },
  });
};

export const useCreateAssetContact = (assetId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssetContactDto) =>
      ContactService.createAssetContact(assetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contactKeys.assetContacts(assetId),
      });
      toast.success('Contact added to asset successfully');
    },
    onError: (error) => {
      console.error('Error creating asset contact:', error);
      toast.error('Failed to add contact to asset');
    },
  });
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateContactDto>;
    }) => ContactService.updateContact(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact updated successfully');
    },
    onError: (error) => {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    },
  });
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ContactService.deleteContact(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.removeQueries({ queryKey: contactKeys.detail(id) });
      toast.success('Contact deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    },
  });
};
